/* ---------------------------------------------------------------------------
   A load rating, drawn.

   The same table the result panel prints — every check, every vehicle, both
   levels — read two ways, plus the headline figures:

     · how far each check is from 1.0, at inventory and operating, for one
       vehicle at a time;
     · what each vehicle may weigh on the bridge, against its own gross weight
       and against the limit the structure is signed for.

   NOTHING IS COMPUTED HERE THAT THE ENGINE DOES NOT DECIDE. The factors, the
   governing cell and the posting are read off `RatingResult` as the engine left
   them; this module only picks cells and draws them.

   ONE COLOUR SCHEME — below 1.0, 1.0 to 1.2, 1.2 and over — from the status
   tokens. Every figure is also printed or on hover, so no reading depends on
   the colour alone.
   --------------------------------------------------------------------------- */

import { h } from '../../core/dom.ts';
import { card, cardHead, chip, statTile } from '../../components/widgets.ts';
import { chartBox, hbar, niceTicks, r1, scale, sv } from '../../components/chart.ts';
import {
  allVehicles,
  displayRf,
  displayTonnes,
  type Check,
  type RatingFactor,
  type RatingResult,
  type StandardsProfile,
  type VehicleId,
} from '../../cbplr/index.ts';
import { actions } from '../actions.ts';
import type { State } from '../store.ts';

type Tone = 'crit' | 'fair' | 'good';

function tone(rf: number): Tone {
  return rf < 1 ? 'crit' : rf < 1.2 ? 'fair' : 'good';
}

const TONE_LABEL: Readonly<Record<Tone, string>> = {
  crit: 'Below 1.0',
  fair: '1.0 to 1.2',
  good: '1.2 and over',
};

/** The rating factor axis stops here; a longer bar is cut and marked. */
const RF_MAX = 2.5;

export interface RatingChartsInput {
  result: RatingResult;
  /** The load limit the structure is signed for today, tonnes, where the register holds one. */
  signed?: number | null;
}

/** Everything drawn, in reading order, for the inside of a card. */
export function ratingCharts(s: Readonly<State>, c: RatingChartsInput): HTMLElement {
  const vehicle = s.chartVehicle;
  return h(
    'div',
    { class: 'rtc' },
    headline(c.result, c.signed ?? null),
    vehiclePicker(s.profile, vehicle),
    rfChart(c.result, vehicle, s.profile),
    postingChart(c.result, c.signed ?? null),
  );
}

/* --------------------------- the headline --------------------------- */

function headline(result: RatingResult, signed: number | null): HTMLElement {
  const g = result.governing;
  /* Never below zero (QA 2026-09-30): a section that cannot carry its own
     weight carries nothing across it, which is what postingTonnes posts too. */
  const lowestRaw = result.posting.reduce<number | null>(
    (min, p) => (p.unrestricted ? min : min === null ? p.tonnes : Math.min(min, p.tonnes)),
    null,
  );
  const lowest = lowestRaw === null ? null : Math.max(0, lowestRaw);
  const byCheck = worstByCheck(result);
  const failing = [...byCheck.values()].filter((rf) => rf < 1).length;
  return h(
    'div',
    { class: 'rtc__kpis' },
    statTile({
      label: 'Governing rating factor',
      value: g === null ? '—' : displayRf(g.rf),
      unit: '',
      ...(g === null ? {} : { sub: `${g.checkLabel} · ${g.level} · ${vehicleName(result, g.vehicle)}` }),
    }),
    statTile({
      label: 'Safe load',
      value: lowest === null ? 'Full' : displayTonnes(lowest),
      unit: lowest === null ? '' : 't',
    }),
    statTile({
      label: 'Signed limit',
      value: signed === null ? '—' : String(signed),
      unit: signed === null ? '' : 't',
      ...(signed === null || lowest === null
        ? {}
        : { sub: signed <= lowest ? `within the rated ${displayTonnes(lowest)} t` : `above the rated ${displayTonnes(lowest)} t` }),
    }),
    statTile({
      label: 'Checks below 1.0',
      value: String(failing),
      unit: `of ${byCheck.size}`,
    }),
  );
}

/** The lowest inventory factor each check reaches over every vehicle. */
function worstByCheck(result: RatingResult): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of result.table) {
    if (r.level !== 'inventory') continue;
    const key = `${r.check}|${r.method}`;
    out.set(key, Math.min(out.get(key) ?? Infinity, r.rf));
  }
  return out;
}

function vehicleName(result: RatingResult, id: VehicleId): string {
  return result.posting.find((p) => p.vehicle.id === id)?.vehicle.name ?? id;
}

function vehiclePicker(profile: StandardsProfile, vehicle: VehicleId): HTMLElement {
  return h(
    'div',
    { class: 'rtc__pick' },
    h('span', { class: 'rtc__pickLabel', text: 'Rating factors for' }),
    ...allVehicles(profile).map((v) =>
      chip(`${v.name} · ${v.tonnes} t`, v.id === vehicle, () => actions.setChartVehicle(v.id)),
    ),
  );
}

/* --------------------------- the cells one vehicle reads --------------------------- */

interface Row {
  check: Check;
  vehicle: VehicleId;
  label: string;
  inventory: RatingFactor | null;
  operating: RatingFactor | null;
}

function rowsFor(result: RatingResult, vehicle: VehicleId): Row[] {
  const methods = new Set(result.checks.map((c) => c.method));
  return result.checks.map((check) => {
    const cell = (level: 'inventory' | 'operating') =>
      result.table.find(
        (r) => r.check === check.id && r.method === check.method && r.vehicle === vehicle && r.level === level,
      ) ?? null;
    return {
      check,
      vehicle,
      label: methods.size > 1 ? `${check.label} · ${check.method}` : check.label,
      inventory: cell('inventory'),
      operating: cell('operating'),
    };
  });
}

function tipFor(row: Row, vehicleLabel: string, governing: RatingFactor | null): string {
  const live = row.check.live[row.vehicle];
  return [
    row.label,
    `${vehicleLabel}: inventory ${row.inventory ? displayRf(row.inventory.rf) : '—'} · operating ${
      row.operating ? displayRf(row.operating.rf) : '—'
    }`,
    `Capacity ${fmt(row.check.capacity.inventory)} at inventory · dead ${fmt(row.check.dead)} · live ${
      live === undefined ? '—' : fmt(live)
    }`,
    row.check.governedBy ? `Set by ${row.check.governedBy}` : '',
    governing !== null && governing.check === row.check.id && governing.method === row.check.method
      ? 'Governs the rating'
      : '',
  ]
    .filter((l) => l !== '')
    .join('\n');
}

function fmt(v: number): string {
  return v.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

/* --------------------------- rating factors --------------------------- */

function rfChart(result: RatingResult, vehicle: VehicleId, profile: StandardsProfile): HTMLElement {
  const rows = rowsFor(result, vehicle).filter((r) => r.inventory !== null || r.operating !== null);
  const vLabel = allVehicles(profile).find((v) => v.id === vehicle)?.name ?? vehicle;
  const W = 760;
  const labelW = 210;
  const valueW = 92;
  const rowH = 26;
  const top = 22;
  const H = top + rows.length * rowH + 8;
  const x0 = labelW;
  const x1 = W - valueW;
  const x = scale(0, RF_MAX, x0, x1);
  const clamp = (rf: number) => Math.max(0, Math.min(RF_MAX, rf));
  const gov = result.governing;

  const ticks = [0, 0.5, 1, 1.5, 2, 2.5];
  const grid = ticks.map((v) =>
    sv(
      'g',
      {},
      sv('line', { x1: r1(x(v)), x2: r1(x(v)), y1: top - 4, y2: H - 6, class: v === 1 ? 'rtc__one' : 'pchart__grid' }),
      sv('text', { x: r1(x(v)), y: top - 9, class: 'pchart__tick', 'text-anchor': 'middle' }, v === RF_MAX ? `${v}+` : String(v)),
    ),
  );

  const marks = rows.map((row, i) => {
    const y = top + i * rowH;
    const inv = row.inventory?.rf ?? null;
    const op = row.operating?.rf ?? null;
    const isGov = gov !== null && gov.check === row.check.id && gov.method === row.check.method;
    const barY = y + (rowH - 12) / 2;
    return sv(
      'g',
      { 'data-tip': tipFor(row, vLabel, gov) },
      sv('rect', { x: 0, y, width: W, height: rowH, class: 'pchart__hit' }),
      sv('text', { x: labelW - 10, y: y + rowH / 2 + 4, class: isGov ? 'pchart__value' : 'pchart__label', 'text-anchor': 'end' }, row.label),
      inv === null || clamp(inv) === 0
        ? null
        : sv('path', { d: hbar(x0, barY, Math.max(2, x(clamp(inv)) - x0), 12), class: `rtc__fill rtc__fill--${tone(inv)}` }),
      inv !== null && inv > RF_MAX
        ? sv('path', { d: `M${r1(x1 - 6)},${r1(barY - 1)}l4,7l-4,7`, class: 'rtc__cut' })
        : null,
      op === null
        ? null
        : sv('line', {
            x1: r1(x(clamp(op))),
            x2: r1(x(clamp(op))),
            y1: r1(barY - 3),
            y2: r1(barY + 15),
            class: 'rtc__op',
          }),
      sv(
        'text',
        { x: W - 4, y: y + rowH / 2 + 4, class: 'pchart__value', 'text-anchor': 'end' },
        `${inv === null ? '—' : displayRf(inv)} · ${op === null ? '—' : displayRf(op)}`,
      ),
    );
  });

  const svg = sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': `Rating factor of each check for ${vLabel}, inventory and operating.`,
    },
    ...grid,
    ...marks,
  );

  return section(
    'Rating factor by check',
    legend([
      ...(['crit', 'fair', 'good'] as const).map((k) => ({ kind: k, label: `Inventory, ${TONE_LABEL[k].toLowerCase()}` })),
      { kind: 'op', label: 'Operating' },
      { kind: 'one', label: 'RF 1.0' },
    ]),
    chartBox(svg),
  );
}

/* --------------------------- safe load --------------------------- */

function postingChart(result: RatingResult, signed: number | null): HTMLElement {
  const W = 760;
  const labelW = 150;
  const valueW = 150;
  const rowH = 34;
  const top = 26;
  const H = top + result.posting.length * rowH + 8;
  const max = Math.max(...result.posting.map((p) => p.vehicle.tonnes), signed ?? 0);
  const ticks = niceTicks(max);
  const xMax = ticks[ticks.length - 1]!;
  const x0 = labelW;
  const x1 = W - valueW;
  const x = scale(0, xMax, x0, x1);

  const grid = ticks.map((v) =>
    sv(
      'g',
      {},
      sv('line', { x1: r1(x(v)), x2: r1(x(v)), y1: top - 4, y2: H - 6, class: 'pchart__grid' }),
      sv('text', { x: r1(x(v)), y: top - 9, class: 'pchart__tick', 'text-anchor': 'middle' }, `${v} t`),
    ),
  );

  const marks = result.posting.map((p, i) => {
    const y = top + i * rowH;
    const barY = y + (rowH - 14) / 2;
    const by = p.governedBy;
    const tip = [
      p.vehicle.name,
      `Gross ${p.vehicle.tonnes} t · ${p.unrestricted ? 'carried at its own weight' : `limited to ${displayTonnes(Math.max(0, p.tonnes))} t`}`,
      by ? `Set by ${by.checkLabel}, ${by.method} ${by.level}, RF ${displayRf(by.rf)}` : '',
    ]
      .filter((l) => l !== '')
      .join('\n');
    return sv(
      'g',
      { 'data-tip': tip },
      sv('rect', { x: 0, y, width: W, height: rowH, class: 'pchart__hit' }),
      sv('text', { x: labelW - 10, y: y + rowH / 2 + 4, class: 'pchart__label', 'text-anchor': 'end' }, p.vehicle.name),
      sv('path', { d: hbar(x0, barY, x(p.vehicle.tonnes) - x0, 14), class: 'rtc__track' }),
      sv('path', {
        d: hbar(x0, barY, Math.max(2, x(Math.max(0, p.tonnes)) - x0), 14),
        class: `rtc__fill rtc__fill--${p.unrestricted ? 'good' : 'crit'}`,
      }),
      sv(
        'text',
        { x: W - 4, y: y + rowH / 2 + 4, class: 'pchart__value', 'text-anchor': 'end' },
        p.unrestricted ? `${p.vehicle.tonnes} t · unrestricted` : `${displayTonnes(Math.max(0, p.tonnes))} t of ${p.vehicle.tonnes} t`,
      ),
    );
  });

  const sign =
    signed === null
      ? null
      : sv(
          'g',
          {},
          sv('line', { x1: r1(x(signed)), x2: r1(x(signed)), y1: top - 2, y2: H - 4, class: 'rtc__sign' }),
          sv('text', { x: r1(x(signed)) + 4, y: H + 10, class: 'pchart__note' }, `Signed ${signed} t`),
        );

  const svg = sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H + 14}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': `Rated load limit of each vehicle against its gross weight${signed === null ? '' : ` and the signed ${signed} t`}.`,
    },
    ...grid,
    ...marks,
    sign,
  );

  return section(
    'Safe load by vehicle',
    legend([
      { kind: 'crit', label: 'Rated limit, restricted' },
      { kind: 'good', label: 'Carried at full weight' },
      { kind: 'track', label: 'Gross weight' },
      ...(signed === null ? [] : [{ kind: 'sign' as const, label: 'Signed limit' }]),
    ]),
    chartBox(svg),
  );
}

/* --------------------------- furniture --------------------------- */

type KeyKind = Tone | 'op' | 'one' | 'track' | 'sign';

function legend(items: { label: string; kind: KeyKind }[]): HTMLElement {
  return h(
    'div',
    { class: 'pchart__legend rtc__legend' },
    ...items.map((i) =>
      h('span', { class: 'pchart__key' }, h('span', { class: `pchart__swatch rtc__key--${i.kind}` }), i.label),
    ),
  );
}

function section(title: string, key: HTMLElement, chart: HTMLElement): HTMLElement {
  return h('div', { class: 'rtc__sec' }, cardHead(title), key, chart);
}

/** The whole card, for a rating on record. */
export function ratingSheet(
  s: Readonly<State>,
  title: string,
  hint: string | undefined,
  c: RatingChartsInput,
  ...trailing: HTMLElement[]
): HTMLElement {
  return card({ pad: true, class: 'rtc-card' }, cardHead(title, hint), ratingCharts(s, c), ...trailing);
}
